import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { getLogs, getStateStats, resetState, StateStats } from '@/lib/api'
import { Download, Copy, RefreshCw, Trash2, Database, Clock, FileText, Activity } from 'lucide-react'
import { toast } from '@/components/ui/toaster'

type Tab = 'events' | 'decisions' | 'executions' | 'digest'

export default function Logs() {
  const [activeTab, setActiveTab] = useState<Tab>('events')
  const [loading, setLoading] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [stats, setStats] = useState<StateStats | null>(null)
  const [logs, setLogs] = useState<{
    events: unknown[]
    decisions: unknown[]
    executions: unknown[]
    digest: unknown[]
  }>({
    events: [],
    decisions: [],
    executions: [],
    digest: [],
  })

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const [logsData, statsData] = await Promise.all([getLogs(), getStateStats()])
      setLogs(logsData)
      setStats(statsData)
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset all state? This will clear events, cooldowns, digest, and audit log.')) {
      return
    }
    setResetting(true)
    try {
      await resetState()
      toast({ title: 'State Reset', description: 'All state cleared successfully' })
      await fetchLogs()
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' })
    } finally {
      setResetting(false)
    }
  }

  useEffect(() => {
    fetchLogs()
  }, [])

  const handleExport = () => {
    const data = logs[activeTab]
    const jsonl = data.map((item) => JSON.stringify(item)).join('\n')
    const blob = new Blob([jsonl], { type: 'application/jsonl' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${activeTab}.jsonl`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleCopyLast = () => {
    const data = logs[activeTab]
    if (data.length === 0) {
      toast({ title: 'No data', description: 'Nothing to copy' })
      return
    }
    navigator.clipboard.writeText(JSON.stringify(data[data.length - 1], null, 2))
    toast({ title: 'Copied', description: 'Last item copied to clipboard' })
  }

  const renderContent = () => {
    const data = logs[activeTab]

    if (data.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No {activeTab} recorded yet.
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {data.map((item, i) => (
          <pre key={i} className="text-xs bg-muted p-3 rounded overflow-x-auto">
            {JSON.stringify(item, null, 2)}
          </pre>
        ))}
      </div>
    )
  }

  return (
    <div className="container py-8">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl">Audit Logs</h1>
            <p className="text-muted-foreground">View recorded events, decisions, and executions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="destructive" onClick={handleReset} disabled={resetting}>
              <Trash2 className={`h-4 w-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
              Reset State
            </Button>
          </div>
        </div>

        {/* State Stats Bar */}
        {stats && (
          <div className="grid grid-cols-5 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stats.events}</div>
                  <div className="text-xs text-muted-foreground">Events</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stats.executions}</div>
                  <div className="text-xs text-muted-foreground">Executions</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stats.cooldowns}</div>
                  <div className="text-xs text-muted-foreground">Active Cooldowns</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stats.digest}</div>
                  <div className="text-xs text-muted-foreground">Digest Items</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{stats.audit_log}</div>
                  <div className="text-xs text-muted-foreground">Audit Records</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {(['events', 'decisions', 'executions', 'digest'] as Tab[]).map((tab) => (
                  <Button
                    key={tab}
                    variant={activeTab === tab ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    <Badge variant="secondary" className="ml-2">
                      {logs[tab].length}
                    </Badge>
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-1" />
                  Download JSONL
                </Button>
                <Button variant="outline" size="sm" onClick={handleCopyLast}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Last
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {renderContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
